import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Save, Lock, Mail, CheckCircle, Server, ChevronDown, ChevronUp, AlertCircle, Shield } from 'lucide-react'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import apiClient from '@/utils/api'
import { useToast } from '@/components/Toast'

// ── Per-environment AWS config ─────────────────────────────────────────────
interface EnvConfig {
  environment: 'dev' | 'staging' | 'prod'
  label: string
  description: string
  aws_region: string
  aws_account_id: string
  aws_access_key_id: string
  aws_secret_access_key: string
  aws_role_arn: string
  bedrock_endpoint: string
  enabled: boolean
  configured: boolean
}

const ENV_META: Record<string, { color: string; badgeClass: string; title: string; hint: string }> = {
  dev:     { color: 'blue',   badgeClass: 'bg-blue-100 text-blue-700',    title: 'Development',  hint: 'Used for local testing and experimentation. Inherits global AWS credentials if no overrides are set.' },
  staging: { color: 'yellow', badgeClass: 'bg-yellow-100 text-yellow-700', title: 'Staging',      hint: 'Pre-production environment for QA and integration testing. Can point to a separate AWS account or region.' },
  prod:    { color: 'green',  badgeClass: 'bg-green-100 text-green-700',   title: 'Production',   hint: 'Live environment serving real traffic. Strongly recommended to use a dedicated AWS account and IAM Role.' },
}

const AWS_REGIONS = [
  { value: '',            label: '— Inherit global region —' },
  { value: 'us-east-1',  label: 'US East (N. Virginia)' },
  { value: 'us-east-2',  label: 'US East (Ohio)' },
  { value: 'us-west-1',  label: 'US West (N. California)' },
  { value: 'us-west-2',  label: 'US West (Oregon)' },
  { value: 'ca-central-1', label: 'Canada (Central)' },
  { value: 'eu-west-1',  label: 'EU (Ireland)' },
  { value: 'eu-west-2',  label: 'EU (London)' },
  { value: 'eu-central-1', label: 'EU (Frankfurt)' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
]

const blankEnv = (environment: 'dev' | 'staging' | 'prod'): EnvConfig => ({
  environment, label: '', description: '', aws_region: '', aws_account_id: '',
  aws_access_key_id: '', aws_secret_access_key: '', aws_role_arn: '',
  bedrock_endpoint: '', enabled: true, configured: false,
})

interface Settings {
  // API Keys
  anthropic_api_key: string
  openai_api_key: string
  google_api_key: string

  // AWS Credentials
  aws_access_key_id: string
  aws_secret_access_key: string
  aws_region: string

  // Email / SMTP
  smtp_host: string
  smtp_port: number
  smtp_user: string
  smtp_pass: string
  smtp_from: string
  smtp_configured: boolean

  // Default Model Settings
  default_model: string
  default_temperature: number
  default_max_tokens: number

  // Permissions
  can_edit_api_keys: boolean
}

export const Settings = () => {
  const { toast } = useToast()
  const [settings, setSettings] = useState<Settings>({
    anthropic_api_key: '',
    openai_api_key: '',
    google_api_key: '',
    aws_access_key_id: '',
    aws_secret_access_key: '',
    aws_region: 'us-east-1',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_pass: '',
    smtp_from: '',
    smtp_configured: false,
    default_model: 'anthropic.claude-3-haiku-20240307-v1:0',
    default_temperature: 0.7,
    default_max_tokens: 2048,
    can_edit_api_keys: false,
  })
  const [isSavingSmtp, setIsSavingSmtp] = useState(false)

  const [isSaving, setIsSaving] = useState(false)
  const [showKeys, setShowKeys] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Per-environment AWS config state
  const [envConfigs, setEnvConfigs] = useState<Record<string, EnvConfig>>({
    dev:     blankEnv('dev'),
    staging: blankEnv('staging'),
    prod:    blankEnv('prod'),
  })
  const [expandedEnv, setExpandedEnv] = useState<string | null>(null)
  const [savingEnv, setSavingEnv] = useState<string | null>(null)
  const [envErrors, setEnvErrors] = useState<Record<string, string>>({})

  // Enterprise tenant caps (UTC month) — enforced on usage POST
  const [enterpriseSpendCap, setEnterpriseSpendCap] = useState('')
  const [enterpriseInvCap, setEnterpriseInvCap] = useState('')
  const [savingEnterprise, setSavingEnterprise] = useState(false)

  // Logged-in change password (Cognito / local)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [passwordFormError, setPasswordFormError] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const [settingsRes, envRes] = await Promise.allSettled([
        apiClient.get('/api/v1/settings'),
        apiClient.get('/api/v1/settings/environments'),
      ])

      if (settingsRes.status === 'fulfilled') {
        const data = settingsRes.value.data
        setSettings({
          ...data,
          anthropic_api_key:     data.anthropic_api_key     || '',
          openai_api_key:        data.openai_api_key        || '',
          google_api_key:        data.google_api_key        || '',
          aws_access_key_id:     data.aws_access_key_id     || '',
          aws_secret_access_key: data.aws_secret_access_key || '',
          smtp_host:             data.smtp_host             || '',
          smtp_port:             data.smtp_port             ?? 587,
          smtp_user:             data.smtp_user             || '',
          smtp_pass:             data.smtp_pass             || '',
          smtp_from:             data.smtp_from             || '',
          smtp_configured:       data.smtp_configured       ?? false,
        })
        if (data.can_edit_api_keys) {
          try {
            const ep = await apiClient.get<{ monthly_spend_cap_usd: number | null; monthly_invocation_cap: number | null }>(
              '/api/v1/settings/enterprise-policy'
            )
            setEnterpriseSpendCap(
              ep.data.monthly_spend_cap_usd != null && ep.data.monthly_spend_cap_usd !== undefined
                ? String(ep.data.monthly_spend_cap_usd)
                : ''
            )
            setEnterpriseInvCap(
              ep.data.monthly_invocation_cap != null && ep.data.monthly_invocation_cap !== undefined
                ? String(ep.data.monthly_invocation_cap)
                : ''
            )
          } catch {
            /* non-fatal */
          }
        }
      }

      if (envRes.status === 'fulfilled') {
        const list: EnvConfig[] = envRes.value.data
        const map: Record<string, EnvConfig> = { dev: blankEnv('dev'), staging: blankEnv('staging'), prod: blankEnv('prod') }
        for (const cfg of list) {
          map[cfg.environment] = {
            ...blankEnv(cfg.environment as 'dev' | 'staging' | 'prod'),
            ...cfg,
            label:                 cfg.label                 || '',
            description:           cfg.description           || '',
            aws_region:            cfg.aws_region            || '',
            aws_account_id:        cfg.aws_account_id        || '',
            aws_access_key_id:     cfg.aws_access_key_id     || '',
            aws_secret_access_key: cfg.aws_secret_access_key || '',
            aws_role_arn:          cfg.aws_role_arn          || '',
            bedrock_endpoint:      cfg.bedrock_endpoint      || '',
          }
        }
        setEnvConfigs(map)
      }
    } catch (err: any) {
      console.error('Failed to load settings:', err)
      setError('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveEnvConfig = async (env: string) => {
    setSavingEnv(env)
    setEnvErrors(prev => ({ ...prev, [env]: '' }))
    try {
      const cfg = envConfigs[env]
      const payload = {
        label:                 cfg.label || null,
        description:           cfg.description || null,
        aws_region:            cfg.aws_region || null,
        aws_account_id:        cfg.aws_account_id || null,
        aws_access_key_id:     cfg.aws_access_key_id || null,
        aws_secret_access_key: cfg.aws_secret_access_key === '***' ? '***' : (cfg.aws_secret_access_key || null),
        aws_role_arn:          cfg.aws_role_arn || null,
        bedrock_endpoint:      cfg.bedrock_endpoint || null,
        enabled:               cfg.enabled,
      }
      const res = await apiClient.put(`/api/v1/settings/environments/${env}`, payload)
      const updated = res.data
      setEnvConfigs(prev => ({
        ...prev,
        [env]: {
          ...prev[env],
          aws_secret_access_key: updated.aws_secret_access_key || '',
          configured: updated.configured,
        },
      }))
      toast.success(`${ENV_META[env].title} environment saved`)
    } catch (err: any) {
      const msg = err.response?.data?.detail || `Failed to save ${env} config`
      setEnvErrors(prev => ({ ...prev, [env]: msg }))
      toast.error('Save failed', msg)
    } finally {
      setSavingEnv(null)
    }
  }

  const handleSaveEnterprisePolicy = async () => {
    if (!settings.can_edit_api_keys) {
      toast.warning('Permission denied', 'Only admins can edit enterprise policy')
      return
    }
    setSavingEnterprise(true)
    try {
      const payload: { monthly_spend_cap_usd?: number | null; monthly_invocation_cap?: number | null } = {}
      if (enterpriseSpendCap.trim() === '') payload.monthly_spend_cap_usd = null
      else {
        const v = parseFloat(enterpriseSpendCap)
        if (Number.isNaN(v) || v < 0) {
          toast.error('Invalid cap', 'Monthly spend cap must be a non-negative number or empty.')
          setSavingEnterprise(false)
          return
        }
        payload.monthly_spend_cap_usd = v
      }
      if (enterpriseInvCap.trim() === '') payload.monthly_invocation_cap = null
      else {
        const n = parseInt(enterpriseInvCap, 10)
        if (Number.isNaN(n) || n < 1) {
          toast.error('Invalid cap', 'Invocation cap must be a positive integer or empty.')
          setSavingEnterprise(false)
          return
        }
        payload.monthly_invocation_cap = n
      }
      await apiClient.put('/api/v1/settings/enterprise-policy', payload)
      toast.success('Enterprise policy saved', 'Caps apply to the current UTC calendar month.')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Save failed'
      toast.error('Save failed', typeof msg === 'string' ? msg : 'Save failed')
    } finally {
      setSavingEnterprise(false)
    }
  }

  const handleSaveAPIKeys = async () => {
    if (!settings.can_edit_api_keys) {
      toast.warning('Permission denied', 'Only Admins can manage API keys')
      return
    }

    setIsSaving(true)
    setError('')

    try {
      await apiClient.put('/api/v1/settings/api-keys', {
        anthropic_api_key: settings.anthropic_api_key,
        openai_api_key: settings.openai_api_key,
        google_api_key: settings.google_api_key,
      })

      toast.success('API keys saved successfully')
      await loadSettings() // Reload to get masked keys
    } catch (err: any) {
      console.error('Failed to save API keys:', err)
      const msg = err.response?.data?.detail || 'Failed to save API keys'
      setError(msg)
      toast.error('Failed to save API keys', msg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveAWSCredentials = async () => {
    if (!settings.can_edit_api_keys) {
      toast.warning('Permission denied', 'Only Admins can manage AWS credentials')
      return
    }

    setIsSaving(true)
    setError('')

    try {
      await apiClient.put('/api/v1/settings/aws-credentials', {
        aws_access_key_id: settings.aws_access_key_id,
        aws_secret_access_key: settings.aws_secret_access_key,
        aws_region: settings.aws_region,
      })

      toast.success('AWS credentials saved successfully')
      await loadSettings() // Reload to get masked credentials
    } catch (err: any) {
      console.error('Failed to save AWS credentials:', err)
      const msg = err.response?.data?.detail || 'Failed to save AWS credentials'
      setError(msg)
      toast.error('Failed to save AWS credentials', msg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveSmtp = async () => {
    if (!settings.can_edit_api_keys) {
      toast.warning('Permission denied', 'Only Admins can manage email settings')
      return
    }
    setIsSavingSmtp(true)
    setError('')
    try {
      const res = await apiClient.put('/api/v1/settings/smtp', {
        smtp_host: settings.smtp_host,
        smtp_port: settings.smtp_port,
        smtp_user: settings.smtp_user,
        smtp_pass: settings.smtp_pass,
        smtp_from: settings.smtp_from,
      })
      toast.success(res.data.message || 'Email settings saved')
      await loadSettings()
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to save email settings'
      setError(msg)
      toast.error('Failed to save email settings', msg)
    } finally {
      setIsSavingSmtp(false)
    }
  }

  const handleSaveModelSettings = async () => {
    setIsSaving(true)
    setError('')

    try {
      await apiClient.put('/api/v1/settings/model-settings', {
        default_model: settings.default_model,
        default_temperature: settings.default_temperature,
        default_max_tokens: settings.default_max_tokens,
      })

      toast.success('Model settings saved successfully')
    } catch (err: any) {
      console.error('Failed to save model settings:', err)
      const msg = err.response?.data?.detail || 'Failed to save model settings'
      setError(msg)
      toast.error('Failed to save model settings', msg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setPasswordFormError('')

    if (newPassword.length < 8) {
      setPasswordFormError('New password must be at least 8 characters.')
      return
    }
    if (newPassword !== confirmPassword) {
      setPasswordFormError('New password and confirmation do not match.')
      return
    }
    if (currentPassword === newPassword) {
      setPasswordFormError('New password must be different from your current password.')
      return
    }

    setChangingPassword(true)
    try {
      const res = await apiClient.post('/api/v1/auth/change-password', {
        old_password: currentPassword,
        new_password: newPassword,
      })
      toast.success(res.data?.message || 'Password changed successfully')
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : 'Unable to change password. If you are on local/dev auth without a stored password, ask an admin to reset it.'
      setPasswordFormError(msg)
      toast.error('Password change failed', msg)
    } finally {
      setChangingPassword(false)
    }
  }

  const models = [
    {
      value: 'anthropic.claude-3-haiku-20240307-v1:0',
      label: 'Claude 3 Haiku (Recommended Base Model)',
    },
    {
      value: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0',
      label: 'Claude 3.7 Sonnet (Cross-Region Inference Profile)',
    },
    {
      value: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0',
      label: 'Claude 3.5 Sonnet (Cross-Region Inference Profile)',
    },
  ]

  if (loading) {
    return <div className="text-center py-12">Loading settings...</div>
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-gray-600 mt-1">
          Model and environment preferences. BeaCon company AWS connectivity is linked by an
          Owner/Admin under Company Admin — not here.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Admin-only sections */}
      {settings.can_edit_api_keys ? (
        <>
          {/* API Keys */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">API Keys</h2>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Admin Only
              </span>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  Anthropic API Key
                </label>
                <input
                  type={showKeys ? 'text' : 'password'}
                  value={settings.anthropic_api_key}
                  onChange={(e) =>
                    setSettings({ ...settings, anthropic_api_key: e.target.value })
                  }
                  placeholder="sk-ant-..."
                  className="input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Get your key from{' '}
                  <a
                    href="https://console.anthropic.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Anthropic Console
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  OpenAI API Key
                </label>
                <input
                  type={showKeys ? 'text' : 'password'}
                  value={settings.openai_api_key}
                  onChange={(e) =>
                    setSettings({ ...settings, openai_api_key: e.target.value })
                  }
                  placeholder="sk-..."
                  className="input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Get your key from{' '}
                  <a
                    href="https://platform.openai.com/api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    OpenAI Platform
                  </a>
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Google AI API Key
                </label>
                <input
                  type={showKeys ? 'text' : 'password'}
                  value={settings.google_api_key}
                  onChange={(e) =>
                    setSettings({ ...settings, google_api_key: e.target.value })
                  }
                  placeholder="AI..."
                  className="input"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Get your key from{' '}
                  <a
                    href="https://ai.google.dev/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:underline"
                  >
                    Google AI Studio
                  </a>
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={showKeys}
                  onChange={(e) => setShowKeys(e.target.checked)}
                  className="mr-2"
                />
                <label className="text-sm">Show API keys</label>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button onClick={handleSaveAPIKeys} isLoading={isSaving}>
                <Save className="h-5 w-5 mr-2" />
                Save API Keys
              </Button>
            </div>
          </Card>

          {/* Email / SMTP */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold">Email Notifications</h2>
                {settings.smtp_configured && (
                  <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    <CheckCircle className="h-3 w-3" /> Configured
                  </span>
                )}
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Admin Only</span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              When configured, a welcome email with the temporary password is sent automatically
              whenever you create a new user. Leave <strong>SMTP Host</strong> blank to disable
              automatic emails — you can then share credentials manually.
            </p>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-semibold mb-2">SMTP Host</label>
                  <input
                    type="text"
                    value={settings.smtp_host}
                    onChange={e => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Port</label>
                  <input
                    type="number"
                    value={settings.smtp_port}
                    onChange={e => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                    placeholder="587"
                    className="input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">SMTP Username</label>
                <input
                  type="text"
                  value={settings.smtp_user}
                  onChange={e => setSettings({ ...settings, smtp_user: e.target.value })}
                  placeholder="your@email.com"
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  SMTP Password / App Password
                  {settings.smtp_pass === '***' && (
                    <span className="ml-2 text-xs font-normal text-gray-500">(saved — enter a new value to change)</span>
                  )}
                </label>
                <input
                  type="password"
                  value={settings.smtp_pass === '***' ? '' : settings.smtp_pass}
                  onChange={e => setSettings({ ...settings, smtp_pass: e.target.value })}
                  placeholder={settings.smtp_pass === '***' ? '••••••••' : 'App password or SMTP password'}
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">From Address</label>
                <input
                  type="email"
                  value={settings.smtp_from}
                  onChange={e => setSettings({ ...settings, smtp_from: e.target.value })}
                  placeholder="noreply@yourdomain.com"
                  className="input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Shown as the sender in welcome emails. Defaults to the SMTP username if left blank.
                </p>
              </div>

              {!settings.smtp_host && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                  <Mail className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Email notifications are <strong>disabled</strong>. When you create a user,
                    share their email address and temporary password with them manually.
                  </span>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <Button onClick={handleSaveSmtp} isLoading={isSavingSmtp}>
                <Save className="h-5 w-5 mr-2" />
                Save Email Settings
              </Button>
            </div>
          </Card>

          {/* AWS Credentials — legacy Agent Deploy / runtime only (not BeaCon Chat connectivity) */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">AWS Credentials (Agent Deploy)</h2>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Admin Only · Non-BeaCon
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Legacy Agent Deploy / hosted-runtime credentials only — not used for BeaCon Chat.
              To link company AWS for BeaCon, use{' '}
              <Link to="/beacon/admin" className="text-primary-600 hover:underline">
                Company Admin
              </Link>
              .
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2">
                  AWS Access Key ID
                </label>
                <input
                  type={showKeys ? 'text' : 'password'}
                  value={settings.aws_access_key_id}
                  onChange={(e) =>
                    setSettings({ ...settings, aws_access_key_id: e.target.value })
                  }
                  placeholder="AKIA..."
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  AWS Secret Access Key
                </label>
                <input
                  type={showKeys ? 'text' : 'password'}
                  value={settings.aws_secret_access_key}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      aws_secret_access_key: e.target.value,
                    })
                  }
                  placeholder="..."
                  className="input"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2">
                  Default AWS Region
                </label>
                <select
                  value={settings.aws_region}
                  onChange={(e) =>
                    setSettings({ ...settings, aws_region: e.target.value })
                  }
                  className="input"
                >
                  <option value="us-east-1">US East (N. Virginia)</option>
                  <option value="us-west-2">US West (Oregon)</option>
                  <option value="eu-west-1">EU (Ireland)</option>
                  <option value="ap-southeast-1">Asia Pacific (Singapore)</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <Button onClick={handleSaveAWSCredentials} isLoading={isSaving}>
                <Save className="h-5 w-5 mr-2" />
                Save AWS Credentials
              </Button>
            </div>
          </Card>

          {/* Enterprise spend / invocation guardrails */}
          {settings.can_edit_api_keys && (
            <Card>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-indigo-600" />
                  <h2 className="text-xl font-bold">Enterprise guardrails</h2>
                </div>
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Admin Only</span>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Optional monthly caps for <strong>estimated</strong> LLM spend and recorded invocations (UTC calendar month).
                When set, new usage posts that would exceed a cap receive HTTP 429 until the next month or until caps are raised.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Monthly spend cap (USD)</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={enterpriseSpendCap}
                    onChange={e => setEnterpriseSpendCap(e.target.value)}
                    placeholder="e.g. 500 (empty = no cap)"
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Monthly invocation cap</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={enterpriseInvCap}
                    onChange={e => setEnterpriseInvCap(e.target.value)}
                    placeholder="e.g. 100000 (empty = no cap)"
                    className="input"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Clear a field and save to remove that cap. Enforcement uses the same cost estimates as the Usage page.
              </p>
              <div className="flex justify-end mt-4">
                <Button onClick={handleSaveEnterprisePolicy} isLoading={savingEnterprise}>
                  <Save className="h-5 w-5 mr-2" />
                  Save guardrails
                </Button>
              </div>
            </Card>
          )}

          {/* ── Environment AWS Configuration (Agent Deploy only) ───────── */}
          <Card>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Server className="h-5 w-5 text-gray-500" />
                <h2 className="text-xl font-bold">Environment AWS Configuration (Agent Deploy)</h2>
              </div>
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Admin Only · Non-BeaCon
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-5">
              Map each Agent Deploy environment to a specific AWS account, region, and IAM role
              (legacy deploy path). Fields left blank inherit the global Agent Deploy credentials
              above. BeaCon Chat uses the company AWS link under{' '}
              <Link to="/beacon/admin" className="text-primary-600 hover:underline">
                Company Admin
              </Link>
              , not these fields.
            </p>

            <div className="space-y-3">
              {(['dev', 'staging', 'prod'] as const).map((env) => {
                const meta  = ENV_META[env]
                const cfg   = envConfigs[env]
                const isOpen = expandedEnv === env
                const isSavingThis = savingEnv === env
                return (
                  <div key={env} className="border border-gray-200 rounded-lg overflow-hidden">
                    {/* Header row */}
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                      onClick={() => setExpandedEnv(isOpen ? null : env)}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${meta.badgeClass}`}>
                          {meta.title}
                        </span>
                        {cfg.label && <span className="text-sm text-gray-600">{cfg.label}</span>}
                        {cfg.configured
                          ? <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium"><CheckCircle className="h-3.5 w-3.5" /> Configured</span>
                          : <span className="text-xs text-gray-400 italic">Inheriting global</span>
                        }
                      </div>
                      {isOpen ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </button>

                    {/* Expanded form */}
                    {isOpen && (
                      <div className="px-4 py-4 space-y-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 bg-gray-50 rounded p-2">{meta.hint}</p>

                        {envErrors[env] && (
                          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                            <AlertCircle className="h-4 w-4 flex-shrink-0" />
                            {envErrors[env]}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-1">Display Label</label>
                            <input
                              type="text"
                              className="input"
                              placeholder={`e.g. ${meta.title} (us-east-1)`}
                              value={cfg.label}
                              onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], label: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-1">AWS Account ID</label>
                            <input
                              type="text"
                              className="input"
                              placeholder="123456789012"
                              value={cfg.aws_account_id}
                              onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], aws_account_id: e.target.value } }))}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">AWS Region</label>
                          <select
                            className="input"
                            value={cfg.aws_region}
                            onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], aws_region: e.target.value } }))}
                          >
                            {AWS_REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">IAM Role ARN <span className="font-normal text-gray-500">(recommended — avoids storing keys per env)</span></label>
                          <input
                            type="text"
                            className="input"
                            placeholder="arn:aws:iam::123456789012:role/BeaconRole"
                            value={cfg.aws_role_arn}
                            onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], aws_role_arn: e.target.value } }))}
                          />
                          <p className="text-xs text-gray-500 mt-1">Beacon will assume this role when running agents in this environment.</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-semibold mb-1">AWS Access Key ID <span className="font-normal text-gray-500">(override)</span></label>
                            <input
                              type="password"
                              className="input"
                              placeholder="AKIA… (leave blank to inherit)"
                              value={cfg.aws_access_key_id}
                              onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], aws_access_key_id: e.target.value } }))}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-semibold mb-1">
                              AWS Secret Access Key <span className="font-normal text-gray-500">(override)</span>
                              {cfg.aws_secret_access_key === '***' && (
                                <span className="ml-1 text-xs text-gray-400">(saved)</span>
                              )}
                            </label>
                            <input
                              type="password"
                              className="input"
                              placeholder={cfg.aws_secret_access_key === '***' ? '••••••••' : 'leave blank to inherit'}
                              value={cfg.aws_secret_access_key === '***' ? '' : cfg.aws_secret_access_key}
                              onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], aws_secret_access_key: e.target.value } }))}
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">Custom Bedrock Endpoint <span className="font-normal text-gray-500">(optional)</span></label>
                          <input
                            type="url"
                            className="input"
                            placeholder="https://bedrock-runtime.us-east-1.amazonaws.com"
                            value={cfg.bedrock_endpoint}
                            onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], bedrock_endpoint: e.target.value } }))}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold mb-1">Notes</label>
                          <textarea
                            className="input"
                            rows={2}
                            placeholder="Optional notes visible to admins (e.g. 'uses prod-ops AWS account')"
                            value={cfg.description}
                            onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], description: e.target.value } }))}
                          />
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={cfg.enabled}
                              onChange={e => setEnvConfigs(p => ({ ...p, [env]: { ...p[env], enabled: e.target.checked } }))}
                            />
                            Environment enabled (agents can be promoted to / deployed here)
                          </label>
                          <Button onClick={() => handleSaveEnvConfig(env)} isLoading={isSavingThis} size="sm">
                            <Save className="h-4 w-4 mr-1" />
                            Save {meta.title}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Card>
        </>
      ) : (
        <Card className="bg-yellow-50 border-yellow-200">
          <div className="flex items-start gap-3">
            <Lock className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-1">
                Restricted Access
              </h3>
              <p className="text-sm text-yellow-800">
                API keys and Agent Deploy AWS credentials can only be managed by Admin users.
                For BeaCon Chat AWS connectivity, ask an Owner/Admin to link the company account
                under{' '}
                <Link to="/beacon/admin" className="underline hover:text-yellow-950">
                  Company Admin
                </Link>
                — ordinary members do not enter AWS keys here.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Change password — available to every signed-in user */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-gray-500" />
          <h2 className="text-xl font-bold">Change Password</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Update the password for your signed-in account. In production this uses Cognito;
          local/dev auth updates the password stored for admin-created users.
        </p>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">At least 8 characters.</p>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              className="input"
            />
          </div>
          {passwordFormError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{passwordFormError}</span>
            </div>
          )}
          <div className="flex justify-end">
            <Button type="submit" isLoading={changingPassword}>
              <Lock className="h-5 w-5 mr-2" />
              Change Password
            </Button>
          </div>
        </form>
      </Card>

      {/* Default Model Settings - Available to all users */}
      <Card>
        <h2 className="text-xl font-bold mb-4">Default Model Settings</h2>
        <p className="text-sm text-gray-600 mb-4">
          These settings will be used when creating new agents
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">
              Default Model
            </label>
            <select
              value={settings.default_model}
              onChange={(e) =>
                setSettings({ ...settings, default_model: e.target.value })
              }
              className="input"
            >
              {models.map((model) => (
                <option key={model.value} value={model.value}>
                  {model.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Temperature: {settings.default_temperature}
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              value={settings.default_temperature}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  default_temperature: parseFloat(e.target.value),
                })
              }
              className="w-full"
            />
            <p className="text-xs text-gray-500 mt-1">
              Lower = more focused, Higher = more creative
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              Max Tokens
            </label>
            <input
              type="number"
              value={settings.default_max_tokens}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  default_max_tokens: parseInt(e.target.value),
                })
              }
              min="256"
              max="8192"
              step="256"
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Maximum length of responses (256-8192)
            </p>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <Button onClick={handleSaveModelSettings} isLoading={isSaving}>
            <Save className="h-5 w-5 mr-2" />
            Save Model Settings
          </Button>
        </div>
      </Card>

      {/* Info */}
      <Card className="bg-blue-50">
        <h3 className="font-semibold text-blue-900 mb-2">🔒 Security Note</h3>
        <p className="text-sm text-blue-800">
          Settings are securely stored in the backend database and encrypted.
          API keys are masked for display and only admins can manage them.
        </p>
      </Card>
    </div>
  )
}
